export type ExperimentVariant = 'control' | 'experimental';
export type ExperimentArm = 'control' | 'challenger';
export type ExperimentAssignment = {
  experimentId: string;
  variant: ExperimentVariant;
  preview: boolean;
  loadFailed: boolean;
  landerId?: string;
  renderedLanderId?: string;
  runId?: string;
  token?: string;
  // Settles once the backend has recorded this visit, or with undefined when the report failed or was never sent.
  reported?: Promise<ServerAssignment | undefined>;
};
export type LanderExperiment = { id: string; controlLanderId: string; challengerLanderId: string };
export type LanderChoice = {
  experimentId: string | null; arm: ExperimentArm; landerId: string; controlLanderId: string; challengerLanderId: string | null;
};

export function isDefaultExperimentRoute(location: { pathname: string; hash: string }): boolean {
  return location.pathname === '/' && location.hash === '';
}
export function previewVariant(search: string): ExperimentVariant | undefined {
  const value = new URLSearchParams(search).get('variant');
  return value === 'control' || value === 'experimental' ? value : undefined;
}
export function isLanderPreview(search: string): boolean {
  return previewVariant(search) !== undefined || new URLSearchParams(search).has('lander');
}

type ArmStorage = Pick<Storage, 'getItem' | 'setItem'>;
function browserStorage(): ArmStorage | undefined {
  try { return globalThis.localStorage; } catch { return undefined; }
}
// Only the arm is stored, never an identifier, so a reload shows the same lander.
export function chooseLander(experiment: LanderExperiment | null, defaultLanderId: string,
  random: () => number = Math.random, storage: ArmStorage | undefined = browserStorage()): LanderChoice {
  if (!experiment) return {experimentId: null, arm: 'control', landerId: defaultLanderId, controlLanderId: defaultLanderId, challengerLanderId: null};
  const key = `parables-lander-arm:${experiment.id}`;
  let stored: string | null = null;
  try { stored = storage?.getItem(key) ?? null; } catch { /* storage can be blocked */ }
  const arm: ExperimentArm = stored === 'control' || stored === 'challenger' ? stored : random() < 0.5 ? 'challenger' : 'control';
  if (arm !== stored) try { storage?.setItem(key, arm); } catch { /* the visit still renders */ }
  return {
    experimentId: experiment.id, arm, landerId: arm === 'challenger' ? experiment.challengerLanderId : experiment.controlLanderId,
    controlLanderId: experiment.controlLanderId, challengerLanderId: experiment.challengerLanderId,
  };
}

export type ServerAssignment = {
  runId: string; experimentId: string | null; arm: ExperimentArm; landerId: string;
  controlLanderId: string; token: string; enrolledAtUtc: string; conversionEndsAtUtc: string;
};
export function isServerAssignment(value: unknown, choice: LanderChoice): value is ServerAssignment {
  if (!value || typeof value !== 'object') return false;
  const a = value as ServerAssignment;
  return typeof a.runId === 'string' && a.runId.length > 0 && a.runId.length <= 100
    && a.experimentId === choice.experimentId && (a.experimentId === null || a.experimentId === a.runId)
    && a.arm === choice.arm && a.landerId === choice.landerId && a.controlLanderId === choice.controlLanderId
    && typeof a.token === 'string' && a.token.length > 0 && a.token.length <= 240
    && Number.isFinite(Date.parse(a.enrolledAtUtc)) && Number.isFinite(Date.parse(a.conversionEndsAtUtc));
}
// Reports what this browser already rendered. Rendering never waits for it.
export async function reportAssignment(apiBase: string, choice: LanderChoice, transport: typeof fetch = fetch): Promise<ServerAssignment | undefined> {
  try {
    const response = await transport(`${apiBase.replace(/\/$/, '')}/api/analytics/lander/assignment`, {
      method: 'POST', headers: {'Content-Type': 'text/plain;charset=UTF-8'}, credentials: 'omit',
      referrerPolicy: 'no-referrer', cache: 'no-store', signal: AbortSignal.timeout(10000),
      body: JSON.stringify(choice),
    });
    if (!response.ok) return;
    const value: unknown = await response.json();
    return isServerAssignment(value, choice) ? value : undefined;
  } catch { return; }
}
export function sendAssignedEvent(apiBase: string, token: string, event: 'download_click' | 'load_failed', transport: typeof fetch = fetch): void {
  try {
    void transport(`${apiBase.replace(/\/$/, '')}/api/analytics/lander/assigned-event`, {
      method: 'POST', headers: {'Content-Type': 'text/plain;charset=UTF-8'}, credentials: 'omit',
      referrerPolicy: 'no-referrer', keepalive: true, body: JSON.stringify({event, token}),
    }).catch(() => {});
  } catch { /* Navigation never depends on analytics. */ }
}
